import { cn } from '@/utils/cn';

import { shouldShowAxisLabel } from './chart-axis';

export interface SimpleLineChartProps {
  data: Array<{ label: string; value: number }>;
  formatValue?: (value: number) => string;
  height?: number;
  className?: string;
}

type Point = { x: number; y: number };

/**
 * Monotone cubic Hermite spline (Fritsch–Carlson). Preserves monotonicity
 * between points so the curve does not overshoot — important for money trends.
 */
function monotoneCurvePath(points: Point[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0]!.x} ${points[0]!.y}`;

  const n = points.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const slopes: number[] = [];

  for (let i = 0; i < n - 1; i++) {
    const xSpan = points[i + 1]!.x - points[i]!.x;
    dx.push(xSpan);
    dy.push(points[i + 1]!.y - points[i]!.y);
    slopes.push(xSpan === 0 ? 0 : dy[i]! / xSpan);
  }

  // Horizontal end tangents (and for two points) give a soft ease between values.
  const tangents: number[] = new Array(n).fill(0);
  if (n === 2) {
    tangents[0] = 0;
    tangents[1] = 0;
  } else {
    tangents[0] = slopes[0]!;
    tangents[n - 1] = slopes[n - 2]!;
    for (let i = 1; i < n - 1; i++) {
      if (slopes[i - 1]! * slopes[i]! <= 0) {
        tangents[i] = 0;
      } else {
        const w1 = 2 * dx[i]! + dx[i - 1]!;
        const w2 = dx[i]! + 2 * dx[i - 1]!;
        tangents[i] =
          w1 + w2 === 0 ? 0 : (w1 + w2) / (w1 / slopes[i - 1]! + w2 / slopes[i]!);
      }
    }
  }

  let path = `M ${points[0]!.x} ${points[0]!.y}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i]!;
    const p1 = points[i + 1]!;
    const h = dx[i]!;
    const c1x = p0.x + h / 3;
    const c1y = p0.y + (tangents[i]! * h) / 3;
    const c2x = p1.x - h / 3;
    const c2y = p1.y - (tangents[i + 1]! * h) / 3;
    path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p1.x} ${p1.y}`;
  }
  return path;
}

/**
 * Lightweight SVG line chart for trends — no chart library.
 *
 * Non-negative series use a zero baseline so a single point is not glued to the
 * floor of a tall card. Padding keeps points and labels inside the viewBox.
 * Multi-point series use a monotone cubic curve for a smooth flow.
 */
export const SimpleLineChart = ({
  data,
  formatValue = String,
  height = 200,
  className,
}: SimpleLineChartProps) => {
  if (data.length === 0) {
    return <div className={cn(className)} style={{ height }} />;
  }

  const padX = 20;
  const padTop = 20;
  const padBottom = 36;
  const chartWidth = Math.max(data.length * 56, 280);
  const plotHeight = height - padTop - padBottom;
  const plotWidth = chartWidth - padX * 2;

  const values = data.map((d) => d.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  // Money and counts: anchor at zero so sparse days still read as height.
  const minVal = rawMin >= 0 ? 0 : rawMin;
  const maxVal = rawMax === minVal ? minVal + 1 : rawMax;
  const span = maxVal - minVal;

  const points = data.map((item, index) => {
    const x =
      data.length === 1
        ? padX + plotWidth / 2
        : padX + (index / (data.length - 1)) * plotWidth;
    const y = padTop + plotHeight - ((item.value - minVal) / span) * plotHeight;
    return { ...item, x, y };
  });

  const linePath = monotoneCurvePath(points);
  const baselineY = padTop + plotHeight;
  const areaPath = [
    linePath,
    `L ${points[points.length - 1]!.x} ${baselineY}`,
    `L ${points[0]!.x} ${baselineY}`,
    'Z',
  ].join(' ');

  return (
    <div className={cn('w-full min-w-0', className)}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${chartWidth} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="block max-w-full"
      >
        <line
          x1={padX}
          y1={baselineY}
          x2={chartWidth - padX}
          y2={baselineY}
          stroke="var(--border-default)"
          strokeWidth={1}
        />

        {data.length > 1 ? (
          <>
            <path d={areaPath} fill="var(--brand-primary)" opacity={0.12} />
            <path
              d={linePath}
              fill="none"
              stroke="var(--brand-primary)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </>
        ) : (
          <line
            x1={padX}
            y1={points[0]!.y}
            x2={chartWidth - padX}
            y2={points[0]!.y}
            stroke="var(--brand-primary)"
            strokeWidth={2}
            strokeDasharray="4 4"
            opacity={0.45}
          />
        )}

        {points.map((point, index) => {
          const formatted = formatValue(point.value);
          const aria = `${point.label}: ${formatted}`;
          const label =
            point.label.length > 12 ? `${point.label.slice(0, 11)}…` : point.label;
          // Keep value callouts for sparse series only — dense charts use the tooltip.
          const showValue = data.length <= 4;
          // Prefer above the point; flip below when that would clip the top edge.
          const valueAbove = point.y - 12;
          const valueY = valueAbove < padTop + 10 ? point.y + 18 : valueAbove;

          return (
            <g key={`${point.label}-${index}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r={4}
                fill="var(--surface)"
                stroke="var(--brand-primary)"
                strokeWidth={2}
                role="img"
                aria-label={aria}
              >
                <title>{aria}</title>
              </circle>
              {showValue ? (
                <text
                  x={point.x}
                  y={valueY}
                  textAnchor="middle"
                  className="fill-[var(--text-primary)]"
                  style={{ fontSize: 11, fontWeight: 600 }}
                >
                  {formatted}
                </text>
              ) : null}
              {shouldShowAxisLabel(index, data.length) ? (
                <text
                  x={point.x}
                  y={height - 12}
                  textAnchor="middle"
                  className="fill-[var(--text-tertiary)]"
                  style={{ fontSize: 10 }}
                >
                  {label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
